import { apiService } from "./api";

export const routeService = {
  // ===== CORE ROUTE OPERATIONS =====

  // Get all routes with enhanced data
  getAllRoutes: async (params = {}) => {
    try {
      const response = await apiService.get("/routes", { params });
      return response.data || [];
    } catch (error) {
      console.error("Error fetching routes:", error);
      throw new Error(`Failed to fetch routes: ${error.message}`);
    }
  },

  // Get route by ID with full details
  getRouteById: async (routeId) => {
    try {
      const response = await apiService.get(`/routes/${routeId}`);
      return response.data;
    } catch (error) {
      console.error("Error fetching route:", error);
      if (error.response?.status === 404) {
        throw new Error("Route not found");
      }
      throw new Error(`Failed to fetch route: ${error.message}`);
    }
  },

  // Get orders for a specific route
  getRouteOrders: async (routeId) => {
    try {
      const response = await apiService.get(`/routes/${routeId}/orders`);
      return response.data || [];
    } catch (error) {
      console.error("Error fetching route orders:", error);
      if (error.response?.status === 404) {
        return []; // Return empty array if route not found
      }
      throw new Error(`Failed to fetch route orders: ${error.message}`);
    }
  },

  // Get medical professionals with starting points from backend
  getMedicalProfessionalsWithStartingPoints: async () => {
    try {
      const response = await apiService.get(
        "/routes/medical-professionals-with-points"
      );
      return response.data || [];
    } catch (error) {
      console.error("Error fetching MPs with starting points:", error);
      return []; // Return empty array on error
    }
  },

  // Update medical professional starting point
  updateMPStartingPoint: async (mpId, startingPointData) => {
    try {

      const response = await apiService.put(
        `/medical-professionals/${mpId}/starting-point`,
        startingPointData
      );

      return response.data;
    } catch (error) {
      console.error("Error updating MP starting point:", error);
      throw new Error(`Failed to update starting point: ${error.message}`);
    }
  },

  // Generate routes using backend's enhanced optimization
  generateRoutes: async (request) => {
    try {
      const response = await apiService.post("/routes/generate", request);
      return response.data;
    } catch (error) {
      console.error("Error generating routes:", error);
      if (error.response?.status === 400) {
        throw new Error(
          error.response.data?.detail ||
            "No orders available for route generation. Please assign orders to medical professionals first."
        );
      }
      throw new Error(`Route generation failed: ${error.message}`);
    }
  },

  // Route optimization with backend's MP starting points automatically
  optimizeRoutes: async (request) => {
    try {
      const response = await apiService.post("/routes/optimize", request);
      return response.data;
    } catch (error) {
      console.error("Error optimizing routes:", error);
      if (error.response?.status === 400) {
        throw new Error(
          error.response.data?.detail ||
            "No orders available for optimization. Please assign orders to medical professionals first."
        );
      }
      throw new Error(`Route optimization failed: ${error.message}`);
    }
  },

  // Create route groups from suggestions
  createRouteGroups: async (request) => {
    try {
      const response = await apiService.post("/routes/create", request);
      return response.data;
    } catch (error) {
      console.error("Error creating route groups:", error);
      throw new Error(`Failed to create route groups: ${error.message}`);
    }
  },

  // ===== ROUTE MANAGEMENT =====

  // 🔥 ENHANCED: Update route details including schedule
  updateRoute: async (routeId, updateData) => {
    try {

      // Validate and format the update data
      const formattedData = {};

      // Handle basic route info
      if (updateData.name !== undefined) {
        formattedData.name = updateData.name.trim();
      }

      // 🔥 NEW: Handle schedule updates
      if (updateData.scheduled_date !== undefined) {
        // Ensure date is in YYYY-MM-DD format
        let dateStr = updateData.scheduled_date;
        if (updateData.scheduled_date instanceof Date) {
          dateStr = updateData.scheduled_date.toISOString().split("T")[0];
        }

        // Validate date format
        if (!/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
          throw new Error("Invalid date format. Use YYYY-MM-DD format.");
        }

        formattedData.scheduled_date = dateStr;
      }

      if (updateData.scheduled_time_start !== undefined) {
        // Ensure time is in HH:MM format
        let timeStr = updateData.scheduled_time_start;

        // Handle various time formats
        if (typeof timeStr === "string") {
          // Remove seconds if present (HH:MM:SS -> HH:MM)
          if (timeStr.split(":").length === 3) {
            timeStr = timeStr.substring(0, 5);
          }

          // Validate time format
          if (!/^\d{2}:\d{2}$/.test(timeStr)) {
            throw new Error("Invalid start time format. Use HH:MM format.");
          }
        }

        formattedData.scheduled_time_start = timeStr;
      }

      if (updateData.scheduled_time_end !== undefined) {
        // Ensure time is in HH:MM format
        let timeStr = updateData.scheduled_time_end;

        // Handle various time formats
        if (typeof timeStr === "string") {
          // Remove seconds if present (HH:MM:SS -> HH:MM)
          if (timeStr.split(":").length === 3) {
            timeStr = timeStr.substring(0, 5);
          }

          // Validate time format
          if (!/^\d{2}:\d{2}$/.test(timeStr)) {
            throw new Error("Invalid end time format. Use HH:MM format.");
          }
        }

        formattedData.scheduled_time_end = timeStr;
      }

      // Handle medical professional assignment
      if (updateData.medical_professional_id !== undefined) {
        formattedData.medical_professional_id =
          updateData.medical_professional_id;
      }

      // Handle order reordering if order_ids are provided
      if (updateData.order_ids && Array.isArray(updateData.order_ids)) {
        formattedData.order_ids = updateData.order_ids;
      }

      // 🔥 NEW: Validate schedule logic
      if (
        formattedData.scheduled_time_start &&
        formattedData.scheduled_time_end
      ) {
        const startTime = formattedData.scheduled_time_start;
        const endTime = formattedData.scheduled_time_end;

        // Convert to comparable format (minutes since midnight)
        const startMinutes = timeToMinutes(startTime);
        const endMinutes = timeToMinutes(endTime);

        if (startMinutes >= endMinutes) {
          throw new Error("Start time must be before end time.");
        }
      }


      const response = await apiService.put(
        `/routes/${routeId}`,
        formattedData
      );

      return response.data;
    } catch (error) {
      console.error("Error updating route:", error);

      // Enhanced error handling for schedule-related errors
      if (error.message.includes("date format")) {
        throw new Error("Invalid date format. Please use a valid date.");
      } else if (error.message.includes("time format")) {
        throw new Error(
          "Invalid time format. Please use HH:MM format (e.g., 08:30)."
        );
      } else if (error.message.includes("Start time must be before")) {
        throw new Error("Start time must be before end time.");
      } else if (error.response?.status === 400) {
        const detail = error.response.data?.detail;
        if (detail?.includes("schedule")) {
          throw new Error(`Schedule error: ${detail}`);
        } else if (detail?.includes("date")) {
          throw new Error(`Date error: ${detail}`);
        } else if (detail?.includes("time")) {
          throw new Error(`Time error: ${detail}`);
        }
        throw new Error(detail || "Invalid update data");
      } else if (error.response?.status === 404) {
        throw new Error("Route not found");
      } else if (error.response?.status === 403) {
        throw new Error("You don't have permission to update this route");
      }

      throw new Error(`Failed to update route: ${error.message}`);
    }
  },

  // Update route status
  updateRouteStatus: async (routeId, statusData) => {
    try {
      const response = await apiService.put(
        `/routes/${routeId}/status`,
        statusData
      );
      return response.data;
    } catch (error) {
      console.error("Error updating route status:", error);
      throw new Error(`Failed to update route status: ${error.message}`);
    }
  },

  // Assign medical professional to route
  assignMedicalProfessional: async (routeId, assignmentData) => {
    try {
      const response = await apiService.put(
        `/routes/${routeId}/assign`,
        assignmentData
      );
      return response.data;
    } catch (error) {
      console.error("Error assigning medical professional:", error);
      throw new Error(
        `Failed to assign medical professional: ${error.message}`
      );
    }
  },

  // ===== ORDER MANAGEMENT WITHIN ROUTES =====

  // Add order to route
  addOrderToRoute: async (routeId, orderId) => {
    try {
      const response = await apiService.post(
        `/routes/${routeId}/orders/${orderId}`
      );
      return response.data;
    } catch (error) {
      console.error("Error adding order to route:", error);
      throw new Error(`Failed to add order to route: ${error.message}`);
    }
  },

  // Add multiple orders to route
  addOrdersToRoute: async (routeId, orderData) => {
    try {
      const response = await apiService.post(
        `/routes/${routeId}/orders`,
        orderData
      );
      return response.data;
    } catch (error) {
      console.error("Error adding orders to route:", error);
      throw new Error(`Failed to add orders to route: ${error.message}`);
    }
  },

  // Remove order from route
  removeOrderFromRoute: async (routeId, orderId) => {
    try {
      const response = await apiService.delete(
        `/routes/${routeId}/orders/${orderId}`
      );
      return response.data;
    } catch (error) {
      console.error("Error removing order from route:", error);
      throw new Error(`Failed to remove order from route: ${error.message}`);
    }
  },

  // Reorder orders within route
  reorderRouteOrders: async (routeId, orderData) => {
    try {

      // Validate input
      if (!orderData.order_ids || !Array.isArray(orderData.order_ids)) {
        throw new Error("Invalid order data: order_ids array is required");
      }

      if (orderData.order_ids.length === 0) {
        throw new Error("Cannot reorder: no orders provided");
      }

      const response = await apiService.put(
        `/routes/${routeId}/reorder`,
        orderData
      );


      // Validate response
      if (!response.data.success) {
        throw new Error(response.data.message || "Reorder operation failed");
      }

      return response.data;
    } catch (error) {
      console.error("Error reordering route orders:", error);

      // Provide specific error messages based on status codes
      if (error.response?.status === 404) {
        throw new Error("Route not found. Please refresh and try again.");
      } else if (error.response?.status === 403) {
        throw new Error("You don't have permission to modify this route.");
      } else if (error.response?.status === 400) {
        const detail = error.response.data?.detail;
        if (detail?.includes("missing")) {
          throw new Error(
            "Some orders could not be found in this route. Please refresh and try again."
          );
        }
        throw new Error(detail || "Invalid order sequence provided.");
      } else if (error.response?.data?.detail) {
        throw new Error(error.response.data.detail);
      }

      throw new Error(`Failed to reorder route orders: ${error.message}`);
    }
  },

  // ===== DAILY ROUTE OPERATIONS =====

  // Get routes for a specific date and facility
  getDailyRoutes: async (facilityId, scheduledDate) => {
    try {
      const params = {
        facility_id: facilityId,
        date_from: scheduledDate,
        date_to: scheduledDate,
      };

      const response = await apiService.get("/routes", { params });
      return response.data || [];
    } catch (error) {
      console.error("Error fetching daily routes:", error);
      return [];
    }
  },

  // Reoptimize routes using backend's enhanced generation
  reoptimizeRoutesForDate: async (facilityId, scheduledDate, config = {}) => {
    try {

      // Use the enhanced generate endpoint which includes optimization
      const request = {
        scheduledDate: scheduledDate,
        maxRoutes: config.max_routes_per_day || 5,
      };

      // Generate optimized routes using backend's MP starting points
      const result = await routeService.generateRoutes(request);

      if (result?.routes && result.routes.length > 0) {
        // Create new routes from suggestions
        const createRequest = {
          routes: result.routes,
          scheduled_date: scheduledDate,
        };

        return await routeService.createRouteGroups(createRequest);
      }

      return [];
    } catch (error) {
      console.error("Error reoptimizing routes:", error);
      throw new Error(`Failed to reoptimize routes: ${error.message}`);
    }
  },

  // ===== ROUTE CONFIGURATION =====

  // Get facility route configuration
  getFacilityConfig: async (facilityId = null) => {
    try {
      const endpoint = facilityId
        ? `/routes/admin/config/${facilityId}`
        : "/routes/config";
      const response = await apiService.get(endpoint);
      return response.data;
    } catch (error) {
      console.error("Error fetching facility config:", error);
      if (error.response?.status === 404) {
        // Return default config if none exists
        return {
          max_routes_per_day: 3,
          max_orders_per_route: 12,
          driver_start_point: {
            lat: 34.1478,
            lng: -118.3109,
            name: "Main Medical Center",
          },
          route_start_time: "08:00",
          route_end_time: "17:00",
          avoid_rush_hours: true,
          auto_create_routes: true,
          geographic_zones: [],
          min_orders_for_route: 1,
        };
      }
      throw new Error(`Failed to fetch facility config: ${error.message}`);
    }
  },

  // Save facility route configuration
  saveFacilityConfig: async (facilityId, config) => {
    try {
      const endpoint = facilityId
        ? `/routes/admin/config/${facilityId}`
        : "/routes/config";
      const response = await apiService.post(endpoint, config);
      return response.data;
    } catch (error) {
      console.error("Error saving facility config:", error);
      throw new Error(`Failed to save facility config: ${error.message}`);
    }
  },

  // Generate Google Maps URL using backend's MP starting points
  generateMapsUrl: async (routeId, includeStartingPoint = true) => {
    try {
      const [route, orders] = await Promise.all([
        routeService.getRouteById(routeId),
        routeService.getRouteOrders(routeId),
      ]);

      if (!orders || orders.length === 0) {
        throw new Error("No orders found in this route");
      }

      let startPoint = "Main Medical Center, Los Angeles, CA";

      // Get MP starting point from backend with proper data structure handling
      if (includeStartingPoint && route.medical_professional_id) {
        try {
          const mps =
            await routeService.getMedicalProfessionalsWithStartingPoints();
          const mp = mps.find((mp) => mp.id === route.medical_professional_id);


          if (mp?.starting_location) {
            // Handle both coordinate and address formats correctly
            if (mp.starting_location.lat && mp.starting_location.lng) {
              startPoint = `${mp.starting_location.lat},${mp.starting_location.lng}`;
            } else if (mp.starting_location.address) {
              startPoint = mp.starting_location.address;
            } else if (mp.starting_location.name) {
              startPoint = mp.starting_location.name;
            }
          } else if (mp?.has_starting_point) {
            // Fallback: Check if starting point is stored differently
            console.warn(
              "MP has starting point but starting_location is not accessible"
            );

            // Check if it's stored in geographic_area (based on your backend code)
            if (mp.geographic_area?.starting_point) {
              const sp = mp.geographic_area.starting_point;
              if (sp.lat && sp.lng) {
                startPoint = `${sp.lat},${sp.lng}`;
              } else if (sp.address) {
                startPoint = sp.address;
              }
            }
          } else {
          }
        } catch (err) {
          console.warn("Could not get MP starting point, using default:", err);
        }
      }

      // Get valid addresses from orders
      const validAddresses = orders
        .map((order) => order.patient_address)
        .filter(Boolean);

      if (validAddresses.length === 0) {
        throw new Error("No valid addresses found in route orders");
      }

      // Proper URL construction for Google Maps
      const origin = encodeURIComponent(startPoint);

      if (validAddresses.length === 1) {
        const destination = encodeURIComponent(validAddresses[0]);
        return `https://www.google.com/maps/dir/${origin}/${destination}`;
      } else {
        const destinations = validAddresses
          .map((addr) => encodeURIComponent(addr))
          .join("/");

        return `https://www.google.com/maps/dir/${origin}/${destinations}`;
      }
    } catch (error) {
      console.error("Error generating maps URL:", error);
      throw new Error(`Failed to generate maps URL: ${error.message}`);
    }
  },

  // Open route in Google Maps with better error handling
  openRouteInMaps: async (routeId) => {
    try {
      const url = await routeService.generateMapsUrl(routeId);
      window.open(url, "_blank");
      return url; // Return URL for confirmation
    } catch (error) {
      console.error("Error opening route in maps:", error);

      // Provide user-friendly error messages
      let userMessage = "Failed to open route in Google Maps";

      if (error.message.includes("No orders found")) {
        userMessage =
          "Cannot open empty route in Google Maps. Please add orders to this route first.";
      } else if (error.message.includes("No valid addresses")) {
        userMessage =
          "Cannot open route in Google Maps. Please ensure all orders have valid patient addresses.";
      } else if (error.message.includes("too long")) {
        userMessage =
          "Route has too many stops for Google Maps URL. Please try opening individual addresses.";
      }

      throw new Error(userMessage);
    }
  },

  // Get order status breakdown for debugging
  getOrderStatusBreakdown: async (facilityId = null, scheduledDate = null) => {
    try {
      const endpoint = facilityId
        ? `/routes/admin/orders/status/${facilityId}`
        : "/routes/orders/status";

      const params = {};
      if (scheduledDate) {
        params.scheduled_date = scheduledDate;
      }

      const response = await apiService.get(endpoint, { params });
      return response.data;
    } catch (error) {
      console.error("Error fetching order status breakdown:", error);
      throw error;
    }
  },

  // ===== ROUTE ANALYTICS =====

  // Get route statistics
  getRouteStats: async (routeId) => {
    try {
      const route = await routeService.getRouteById(routeId);
      const orders = await routeService.getRouteOrders(routeId);

      const stats = {
        route_id: routeId,
        total_orders: orders.length,
        completed_orders: orders.filter((o) => o.status === "completed").length,
        in_progress_orders: orders.filter((o) => o.status === "in_progress")
          .length,
        pending_orders: orders.filter((o) => o.status === "assigned").length,
        estimated_duration: orders.length * 30, // 30 minutes per order
        completion_rate:
          orders.length > 0
            ? (orders.filter((o) => o.status === "completed").length /
                orders.length) *
              100
            : 0,
        route_efficiency: routeService._calculateRouteEfficiency(orders),
      };

      return stats;
    } catch (error) {
      console.error("Error getting route stats:", error);
      return {
        route_id: routeId,
        total_orders: 0,
        completed_orders: 0,
        in_progress_orders: 0,
        pending_orders: 0,
        estimated_duration: 0,
        completion_rate: 0,
        route_efficiency: 0,
      };
    }
  },

  // ===== UTILITY FUNCTIONS =====

  // Calculate route efficiency
  _calculateRouteEfficiency: (orders) => {
    if (!orders || orders.length === 0) return 0;

    const totalServiceTime = orders.length * 30; // 30 min per order
    const estimatedTravelTime = Math.max(orders.length - 1, 0) * 15; // 15 min between stops
    const totalTime = totalServiceTime + estimatedTravelTime;

    return totalTime > 0 ? Math.round((totalServiceTime / totalTime) * 100) : 0;
  },

  // Format route for display
  formatRouteForDisplay: (route, orders = [], medicalProfessional = null) => {
    return {
      ...route,
      order_count: orders.length,
      completed_count: orders.filter((o) => o.status === "completed").length,
      progress_percentage:
        orders.length > 0
          ? (orders.filter((o) => o.status === "completed").length /
              orders.length) *
            100
          : 0,
      medical_professional_name:
        medicalProfessional?.user?.full_name ||
        (medicalProfessional ? `MP-${medicalProfessional.id}` : "Unassigned"),
      estimated_duration: orders.length * 30,
      total_distance: routeService._estimateRouteDistance(orders),
    };
  },

  // Estimate route distance
  _estimateRouteDistance: (orders) => {
    // Simple estimation: 8 miles average per stop
    return orders.length > 0 ? (orders.length * 8.5).toFixed(1) : "0.0";
  },

  // 🔥 NEW: Validate route schedule data
  validateRouteSchedule: (scheduleData) => {
    const errors = {};

    if (scheduleData.scheduled_date) {
      const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
      if (!dateRegex.test(scheduleData.scheduled_date)) {
        errors.scheduled_date = "Date must be in YYYY-MM-DD format";
      } else {
        // Check if date is not in the past (optional validation)
        const selectedDate = new Date(scheduleData.scheduled_date);
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        // if (selectedDate < today) {
        //   errors.scheduled_date = "Cannot schedule routes in the past";
        // }
      }
    }

    if (scheduleData.scheduled_time_start) {
      const timeRegex = /^\d{2}:\d{2}$/;
      if (!timeRegex.test(scheduleData.scheduled_time_start)) {
        errors.scheduled_time_start = "Start time must be in HH:MM format";
      }
    }

    if (scheduleData.scheduled_time_end) {
      const timeRegex = /^\d{2}:\d{2}$/;
      if (!timeRegex.test(scheduleData.scheduled_time_end)) {
        errors.scheduled_time_end = "End time must be in HH:MM format";
      }
    }

    // Validate start time is before end time
    if (scheduleData.scheduled_time_start && scheduleData.scheduled_time_end) {
      const startMinutes = timeToMinutes(scheduleData.scheduled_time_start);
      const endMinutes = timeToMinutes(scheduleData.scheduled_time_end);

      if (startMinutes >= endMinutes) {
        errors.schedule_logic = "Start time must be before end time";
      }
    }

    return {
      isValid: Object.keys(errors).length === 0,
      errors,
    };
  },

  // Validate route data
  validateRouteData: (routeData) => {
    const errors = {};

    if (!routeData.name?.trim()) {
      errors.name = "Route name is required";
    }

    if (!routeData.scheduled_date) {
      errors.scheduled_date = "Scheduled date is required";
    }

    if (!routeData.facility_id) {
      errors.facility_id = "Facility is required";
    }

    // Add schedule validation
    const scheduleValidation = routeService.validateRouteSchedule(routeData);
    if (!scheduleValidation.isValid) {
      Object.assign(errors, scheduleValidation.errors);
    }

    return {
      isValid: Object.keys(errors).length === 0,
      errors,
    };
  },

  // ===== DEBUG HELPERS =====

  // Get debug info for route
  getRouteDebugInfo: async (routeId) => {
    try {
      const route = await routeService.getRouteById(routeId);
      const orders = await routeService.getRouteOrders(routeId);
      const stats = await routeService.getRouteStats(routeId);

      return {
        route,
        orders,
        stats,
        medical_professional_id: route.medical_professional_id,
        order_count: orders.length,
        debug_timestamp: new Date().toISOString(),
      };
    } catch (error) {
      console.error("Error getting route debug info:", error);
      return {
        error: error.message,
        debug_timestamp: new Date().toISOString(),
      };
    }
  },
};

// 🔥 NEW: Helper function to convert time string to minutes since midnight
function timeToMinutes(timeStr) {
  if (!timeStr || typeof timeStr !== "string") return 0;

  const parts = timeStr.split(":");
  if (parts.length !== 2) return 0;

  const hours = parseInt(parts[0], 10) || 0;
  const minutes = parseInt(parts[1], 10) || 0;

  return hours * 60 + minutes;
}

// 🔥 NEW: Helper function to format time display
export const formatTimeForDisplay = (timeString) => {
  if (!timeString) return "Not set";

  // Handle time strings like "08:00:00" or "08:00"
  const timeParts = timeString.split(":");
  if (timeParts.length >= 2) {
    return `${timeParts[0]}:${timeParts[1]}`;
  }
  return timeString;
};

export const formatDateForDisplay = (dateString) => {
  if (!dateString) return "Not set";

  // Force local time interpretation to avoid timezone shifts
  const date = new Date(dateString + "T00:00:00");

  if (isNaN(date.getTime())) {
    return "Invalid date";
  }

  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "numeric",
    day: "numeric",
  });
};



export default routeService;
